// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState } from 'react';
import { Alert, Button, Modal, TextInput } from '@loa/ui-components';
import { relationshipCashflowApi, type Scenario } from '../../api/relationshipCashflowApi';

/**
 * "Create New Forecast" modal (docs/specs/cashflow-modelling-tab-prd.md R4,
 * Figma 183:28010): forecast name, display-only start date, and the Net/Gross
 * tax basis. Gross is disabled (with a hint) when the tax layer isn't enabled
 * for the brokerage. The client list is informational — forecasts are
 * household-scoped (PRD OQ-1).
 */

interface Props {
  profileId: string;
  /** Client display names for the informational "who is this model for" list. */
  clientNames: string[];
  /** Whether the Gross (Irish/UK tax) basis is available for this brokerage. */
  taxAvailable: boolean;
  onClose: () => void;
  onCreated: (scenario: Scenario) => void;
}

export function CreateForecastModal({ profileId, clientNames, taxAvailable, onClose, onCreated }: Props) {
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  // Cashflows are generated on a Gross (after-tax) basis. Net basis is disabled for now.
  const [basis, setBasis] = useState<'NET' | 'GROSS'>('GROSS');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = () => {
    if (busy || name.trim() === '') return;
    setBusy(true);
    setError(null);
    relationshipCashflowApi
      .createScenario(profileId, {
        name: name.trim(),
        description: null,
        basis,
        displayStartDate: startDate || null,
      })
      .then(onCreated)
      .catch(() => setError('Could not create the forecast.'))
      .finally(() => setBusy(false));
  };

  return (
    <Modal open onClose={onClose} size="small">
      <Modal.Header icon="plus" title="Create New Forecast" onClose={onClose} />
      <Modal.Content>
        <div data-testid="create-forecast-modal" className="flex flex-col gap-md">
          {error && <Alert variant="error">{error}</Alert>}

          {clientNames.length > 0 && (
            <div>
              <p className="text-body-03 font-medium text-black m-0 mb-xs">
                Who is this cashflow model for?
              </p>
              <p data-testid="forecast-clients" className="text-body-03 text-grey-04 m-0">
                {clientNames.join(', ')}
              </p>
            </div>
          )}

          <TextInput
            label="Forecast Name*"
            data-testid="forecast-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Cashflow 1"
            fluid
          />

          <TextInput
            type="date"
            label="Start Date*"
            data-testid="forecast-start-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            fluid
          />

          <fieldset className="border-0 p-0 m-0">
            <legend className="text-body-03 font-medium text-black mb-xs p-0">
              Cashflow Modelling Type<span className="text-error-text">*</span>
            </legend>
            <div className="flex flex-col gap-sm">
              <label
                className="flex items-start gap-sm rounded-lg border border-grey-05 px-sm py-sm cursor-not-allowed opacity-60"
                data-testid="forecast-basis-net"
              >
                <input
                  type="radio"
                  name="basis"
                  checked={basis === 'NET'}
                  disabled
                  onChange={() => setBasis('NET')}
                  className="mt-xs"
                />
                <span className="text-body-03 text-grey-01">
                  <strong>Net Basis</strong>: For all-round cashflow modelling with simple inputs and
                  concise outputs
                  <span data-testid="forecast-basis-net-disabled" className="block text-body-04 text-grey-03">
                    Disabled for now — cashflows are generated on a Gross basis
                  </span>
                </span>
              </label>
              <label
                className={`flex items-start gap-sm rounded-lg border-[1.5px] px-sm py-sm ${
                  taxAvailable
                    ? 'border-lilac-04 cursor-pointer'
                    : 'border-grey-05 cursor-not-allowed opacity-60'
                }`}
                data-testid="forecast-basis-gross"
              >
                <input
                  type="radio"
                  name="basis"
                  checked={basis === 'GROSS'}
                  disabled={!taxAvailable}
                  onChange={() => setBasis('GROSS')}
                  className="mt-xs"
                />
                <span className="text-body-03 text-grey-01">
                  <strong>Gross Basis</strong>: For more detailed and comprehensive modelling with
                  Irish/UK tax calculations
                  {!taxAvailable && (
                    <span data-testid="forecast-gross-unavailable" className="block text-body-04 text-grey-03">
                      Not enabled for your brokerage
                    </span>
                  )}
                </span>
              </label>
            </div>
          </fieldset>

          <div className="flex justify-end gap-sm mt-xs">
            <Button variant="outlined" size="sm" data-testid="forecast-cancel" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              data-testid="forecast-create"
              onClick={create}
              disabled={busy || name.trim() === ''}
            >
              {busy ? 'Creating…' : 'Create Forecast'}
            </Button>
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
}

export default CreateForecastModal;
